// Service Dependencies
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// App Config data
import { AppSettings } from '../../../app.config';

// Rxjs
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface EympBatchUploadItem {
  modelPreparationId: string;
  files: File[];
}

export interface EympUploadParams {
  employeeId: string;
  caseId: string;
  items: EympBatchUploadItem[];
  isEympEu: boolean;
  isEympUs: boolean;
  isTaxReturnFiling: boolean;
  isFileRenameRequired: boolean;
  eympFirstName?: string;
  eympLastName?: string;
  eympPreferredEmail?: string;
  eympPathwayRef?: string;
  uploadType?: string;
}

export interface EympUploadDocumentResponse {
  success: boolean;
  message?: string;
  taxReturnReferenceId?: string;
  exceptionDetails?: string;
}

export interface EympUploadItemResult {
  modelPreparationId: string | null;
  success: boolean;
  taxReturnReferenceId?: string;
  message?: string;
  exceptionDetails?: string;
}

export interface EympUploadResponse {
  results: EympUploadItemResult[];
  estimatedMinutes: number | null;
}

export interface EympStatusResponse {
  success: boolean;
  statusCode?: number;
  statusDescription?: string;
  message?: string;
}

export interface EympProcessStatus {
  eympStatusCode: number;
  eympStatusDescription: string;
  eympTaxReturnReferenceId: string | null;
  hasEympRequest: boolean;
  createdAt: string | null;
}

export interface EympModelPreparationStatusResponse {
  draft: EympProcessStatus | null;
  filed: EympProcessStatus | null;
}

export interface EympProfileLookupResult {
  dbAvailable: boolean;
  firstName: string | null;
  lastName: string | null;
  preferredEmailAddress: string | null;
  clientName:string | null;
  atrioEmails: string[];
  isSpouseCase: boolean;
  resolvedPathwayRef: string | null;
}

export interface EympResolveResult {
  isSpouseCase: boolean;
  mainEmployeePathwayRef: string | null;
  mainEmployeeId: number | null;
}

export interface EympUploadDocumentParams {
  employeeId: string;
  caseId: string;
  isEympEu: boolean;
  isEympUs: boolean;
  files: File[];
  eympFirstName?: string;
  eympLastName?: string;
  eympPreferredEmail?: string;
  eympPathwayRef?: string;
  modelType?: string;
}

@Injectable()
export class EympDeliveryService {

  apiUrl: string;

  constructor(
    private http: HttpClient
  ) {
    this.apiUrl = AppSettings.API_PROD_ENDPOINT;
  }

  upload(params: EympUploadParams): Observable<EympUploadResponse> {
    const formData = new FormData();
    formData.append('employeeId', params.employeeId);
    formData.append('caseId', params.caseId);
    formData.append('isEympEu', String(params.isEympEu));
    formData.append('isEympUs', String(params.isEympUs));
    formData.append('isTaxReturnFiling', String(params.isTaxReturnFiling));
    formData.append('isFileRenameRequired', String(params.isFileRenameRequired));

    const itemsMeta = params.items.map(item => ({
      modelPreparationId: item.modelPreparationId,
      fileNames: item.files.map(f => f.name),
    }));
    formData.append('items', JSON.stringify(itemsMeta));

    for (const item of params.items) {
      for (const file of item.files) {
        formData.append('files', file, file.name);
      }
    }

    if (params.eympFirstName) formData.append('eympFirstName', params.eympFirstName);
    if (params.eympLastName) formData.append('eympLastName', params.eympLastName);
    if (params.eympPreferredEmail) formData.append('eympPreferredEmail', params.eympPreferredEmail);
    if (params.eympPathwayRef) formData.append('eympPathwayRef', params.eympPathwayRef);
    if (params.uploadType) formData.append('uploadType', params.uploadType);

    const requestUrl = this.apiUrl + `atrio/eymp/upload`;

    return this.http.post<EympUploadResponse>(requestUrl, formData)
      .pipe(
        map((res) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('EYMP Upload - Status: ', err.status, ', Error: ', err.error);
          return throwError(err);
        })
      );
  }

  checkStatus(taxReturnReferenceId: string, isEu: boolean): Observable<EympStatusResponse> {
    const body = { taxReturnReferenceId, isEympEu: isEu, isEympUs: !isEu };
    const requestUrl = this.apiUrl + `atrio/eymp/status`;

    return this.http.post<EympStatusResponse>(requestUrl, body)
      .pipe(
        map((res) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('EYMP Status - Status: ', err.status, ', Error: ', err.error);
          return throwError(err);
        })
      );
  }

  getModelPreparationStatus(modelType: string, modelPreparationId: string): Observable<EympModelPreparationStatusResponse> {
    const requestUrl = this.apiUrl + `atrio/eymp/status-by-preparation/${modelType}/${modelPreparationId}`;

    return this.http.get<EympModelPreparationStatusResponse>(requestUrl)
      .pipe(
        map((res) => res),
        catchError((err: HttpErrorResponse) => {
          console.error('EYMP Model Status - Status: ', err.status, ', Error: ', err.error);
          return throwError(err);
        })
      );
  }
  lookupEympProfile(pathwayRef: string, employeeId: number): Observable<EympProfileLookupResult> {
    const requestUrl = this.apiUrl + `atrio/eymp/profile-lookup/${encodeURIComponent(pathwayRef)}/${employeeId}`;
    return this.http.get<EympProfileLookupResult>(requestUrl).pipe(
      catchError((err: HttpErrorResponse) => {
        console.error('EYMP Profile Lookup - Error:', err.status, err.error);
        return throwError(err);
      })
    );
  }

  resolveEmployee(employeeId: number): Observable<EympResolveResult> {
    const requestUrl = this.apiUrl + `atrio/eymp/resolve-employee/${employeeId}`;
    return this.http.get<EympResolveResult>(requestUrl).pipe(
      catchError((err: HttpErrorResponse) => {
        console.error('EYMP Resolve Employee - Error:', err.status, err.error);
        return throwError(err);
      })
    );
  }

  getEympCasesList(employeeId: string | number, year: number, pathwayRef?: string): Observable<{ success: boolean, data: any[], error?: string }> {
    const pathwaySegment = pathwayRef ? `/${encodeURIComponent(pathwayRef)}` : '';
    const requestUrl = this.apiUrl + `employees/eymp/${employeeId}/cases/${year}${pathwaySegment}`;
    return this.http.get<{ success: boolean, data: any[], error?: string }>(requestUrl).pipe(
      catchError((err: HttpErrorResponse) => {
        console.error('EYMP Cases List - Error:', err.status, err.error);
        return throwError(err);
      })
    );
  }

  uploadDocument(params: EympUploadDocumentParams): Observable<EympUploadDocumentResponse> {
    const formData = new FormData();
    formData.append('employeeId', params.employeeId);
    formData.append('caseId', params.caseId);
    formData.append('isEympEu', String(params.isEympEu));
    formData.append('isEympUs', String(params.isEympUs));
    for (const file of params.files) {
      formData.append('files', file, file.name);
    }
    if (params.eympFirstName) formData.append('eympFirstName', params.eympFirstName);
    if (params.eympLastName) formData.append('eympLastName', params.eympLastName);
    if (params.eympPreferredEmail) formData.append('eympPreferredEmail', params.eympPreferredEmail);
    if (params.eympPathwayRef) formData.append('eympPathwayRef', params.eympPathwayRef);
    if (params.modelType) formData.append('modelType', params.modelType);

    return this.http.post<EympUploadDocumentResponse>(this.apiUrl + 'atrio/eymp/upload-document', formData).pipe(
      catchError((err: HttpErrorResponse) => {
        console.error('EYMP Upload Document - Status: ', err.status, ', Error: ', err.error);
        return throwError(err);
      })
    );
  }
}
